import { CheckCircle2, Loader2 } from 'lucide-react';
import { isResearching, type OnboardingDraft } from './api';

export default function ResearchProgress({
  draft,
  onCancel,
  busy = false,
}: {
  draft: OnboardingDraft;
  onCancel?: () => void;
  busy?: boolean;
}) {
  const working = isResearching(draft);
  const stage = draft.progress?.stage;
  const number = (field: string) =>
    typeof draft.progress?.[field] === 'number' ? (draft.progress[field] as number) : 0;
  const websiteSources = draft.sources.filter(
    (source) => source.kind === 'website' && source.status === 'ready'
  );
  const stored = number('stored') || websiteSources.length;
  const processed = number('processed');
  const discovered = number('discovered');
  const partial = draft.sources.some(
    (source) => source.metadata?.truncated || source.metadata?.partial
  );
  const title = !working
    ? draft.status === 'failed'
      ? 'Continue with what you know'
      : 'Your company brief is taking shape'
    : stage === 'analysis'
      ? 'Bringing your company brief together'
      : stage === 'website'
        ? 'Reading your company website'
        : stage === 'documents'
          ? 'Reading your company documents'
          : 'Getting to know your company';
  return (
    <div className={`context-research-status ${working ? '' : 'is-ready'}`} role="status">
      {working ? <Loader2 size={21} className="context-spin" /> : <CheckCircle2 size={21} />}
      <div>
        <strong>{title}</strong>
        <p>
          {working
            ? 'Your draft is saved. You can return to this page while we read the useful details.'
            : 'Review the information alongside you, then fill any important gaps.'}
        </p>
        {(discovered > 0 || stored > 0) && (
          <div className="context-coverage">
            <span>
              <b>{stored}</b> pages saved
            </span>
            <span>
              <b>{processed}</b> pages examined
            </span>
            <span>
              <b>{discovered}</b> pages discovered
            </span>
          </div>
        )}
        {stage === 'website' && typeof draft.progress?.currentUrl === 'string' && (
          <p className="context-current-source" title={draft.progress.currentUrl}>
            Now reading: {draft.progress.currentUrl}
          </p>
        )}
        {discovered > 100 && (
          <p>
            We prioritise up to 100 useful public pages. Remaining coverage is available through the
            source list.
          </p>
        )}
        {partial && (
          <p>
            Some sources were partially read. Check the source details for coverage before approving
            your brief.
          </p>
        )}
        {working && onCancel && (
          <button type="button" className="context-text-button" disabled={busy} onClick={onCancel}>
            Stop research & continue manually
          </button>
        )}
      </div>
    </div>
  );
}
